import { applyRecipientBrandMappings, type RecipientBrandMapping } from "./business-units.ts";
import {
  applyCodeCategoryRules,
  normalizeRows,
  parseAmount,
  type CodeCategoryRuleLike,
  type Direction,
  type Transaction,
  type TransactionStatus,
} from "./finance.ts";
import type { FinancialCodeRule } from "./profit-loss.ts";

export const MANUAL_SOURCE_LABEL = "Input Manual";
export const MANUAL_SOURCE_FILTER_ID = "__manual_entry__";
export const MANUAL_DRAFT_VERSION = 1;

export type ManualTransactionDraft = {
  date: string;
  time: string;
  direction: Direction;
  amount: string;
  paymentRef: string;
  ftNumber: string;
  subject: string;
  internalDescription: string;
  sourceName: string;
  sourceBank: string;
  beneficiaryName: string;
  beneficiaryBank: string;
  sourceAccount: string;
  beneficiaryAccount: string;
  transferType: string;
  balance: string;
  status: TransactionStatus;
  category: string;
  subcategory: string;
  businessUnit: string;
  financialAccount: string;
};

export type ManualDraftEnvelope = {
  version: typeof MANUAL_DRAFT_VERSION;
  savedAt: string;
  data: ManualTransactionDraft;
};

export type ManualDraftErrors = Partial<Record<"direction" | "amount", string>>;

function localDateValue(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function emptyManualTransactionDraft(now = new Date()): ManualTransactionDraft {
  void now;
  return {
    date: "",
    time: "",
    direction: "",
    amount: "",
    paymentRef: "",
    ftNumber: "",
    subject: "",
    internalDescription: "",
    sourceName: "",
    sourceBank: "",
    beneficiaryName: "",
    beneficiaryBank: "",
    sourceAccount: "",
    beneficiaryAccount: "",
    transferType: "",
    balance: "",
    status: "",
    category: "",
    subcategory: "",
    businessUnit: "",
    financialAccount: "",
  };
}

export function manualDraftStorageKey(accountId: string) {
  return `ek-finance-manual-draft-v${MANUAL_DRAFT_VERSION}:${accountId || "local"}`;
}

export function validateManualTransactionDraft(draft: ManualTransactionDraft): ManualDraftErrors {
  const errors: ManualDraftErrors = {};
  if (!draft.direction) errors.direction = "Tipe Debet/Kredit wajib dipilih.";
  if (parseAmount(draft.amount) <= 0) errors.amount = "Nominal harus lebih besar dari nol.";
  return errors;
}

export function readManualDraft(raw: string | null): ManualDraftEnvelope | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ManualDraftEnvelope>;
    if (parsed.version !== MANUAL_DRAFT_VERSION || !parsed.data || typeof parsed.data !== "object" || Array.isArray(parsed.data) || typeof parsed.savedAt !== "string") return null;
    const fallback = emptyManualTransactionDraft();
    const data = parsed.data as Partial<ManualTransactionDraft>;
    const restored = { ...fallback };
    for (const key of Object.keys(fallback) as (keyof ManualTransactionDraft)[]) {
      if (typeof data[key] === "string") restored[key] = data[key] as never;
    }
    return {
      version: MANUAL_DRAFT_VERSION,
      savedAt: parsed.savedAt,
      data: {
        ...restored,
        direction: restored.direction === "DEBIT" || restored.direction === "CREDIT" ? restored.direction : "",
        status: ["SUCCESS", "FAILED", "PENDING"].includes(String(restored.status))
          ? restored.status as TransactionStatus
          : "",
      },
    };
  } catch {
    return null;
  }
}

function statementDateTime(date: string, time: string) {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}${time ? ` ${time}` : ""}`;
}

export function manualDraftToTransaction(
  draft: ManualTransactionDraft,
  options: {
    codeRules: CodeCategoryRuleLike[];
    financialRules: FinancialCodeRule[];
    recipientMappings: RecipientBrandMapping[];
  },
): Transaction {
  const errors = validateManualTransactionDraft(draft);
  if (Object.keys(errors).length) throw new Error("INVALID_MANUAL_TRANSACTION");

  const amount = parseAmount(draft.amount);
  const row: Record<string, unknown> = {
    "No. Referensi": draft.paymentRef.trim(),
    "FT Number": draft.ftNumber?.trim() || "",
    "Waktu Transaksi": statementDateTime(draft.date, draft.time),
    "Nama Pengirim": draft.sourceName.trim(),
    "Bank Pengirim": draft.sourceBank.trim(),
    "Nama Penerima": draft.beneficiaryName.trim(),
    "Bank Penerima": draft.beneficiaryBank.trim(),
    "Rekening Sumber": draft.sourceAccount.trim(),
    "Rekening Tujuan": draft.beneficiaryAccount.trim(),
    Deskripsi: draft.subject.trim(),
    "Jenis Transfer": draft.transferType.trim(),
    Account: MANUAL_SOURCE_LABEL,
    Balance: draft.balance.trim(),
    [draft.direction === "CREDIT" ? "Kredit" : "Debet"]: amount,
  };
  const normalized = normalizeRows([row], MANUAL_SOURCE_LABEL)[0];
  if (!normalized) throw new Error("INVALID_MANUAL_TRANSACTION");

  const categorized = normalized;
  const financialRule = options.financialRules.find((rule) =>
    rule.uniqueCode === categorized.uniqueCode && rule.direction === categorized.direction);
  const financiallyMapped: Transaction = financialRule ? {
    ...categorized,
    financialAccount: financialRule.financialAccount,
    financialMappingSource: "rule",
    businessUnit: financialRule.businessUnit || categorized.businessUnit,
    businessUnitMappingSource: financialRule.businessUnit ? "code" : categorized.businessUnitMappingSource,
  } : categorized;
  const mapped = applyRecipientBrandMappings([financiallyMapped], options.recipientMappings)[0];

  const brands = draft.businessUnit ? draft.businessUnit.split("+").filter((value) => value !== "unassigned") : mapped.businessUnits;
  return applyCodeCategoryRules([{
    ...mapped,
    id: `MANUAL-PREVIEW-${mapped.fingerprint}`,
    internalDescription: draft.internalDescription.trim(),
    category: draft.category || mapped.category,
    subcategory: draft.subcategory || undefined,
    categoryAssignmentSource: draft.category ? "manual" : mapped.categoryAssignmentSource,
    businessUnit: draft.businessUnit ? brands?.length === 1 ? brands[0] : "unassigned" : mapped.businessUnit,
    businessUnits: brands,
    businessUnitMappingSource: draft.businessUnit ? "manual" : mapped.businessUnitMappingSource,
    financialAccount: draft.financialAccount || mapped.financialAccount,
    financialMappingSource: draft.financialAccount ? "manual" : mapped.financialMappingSource,
    sourceFile: MANUAL_SOURCE_LABEL,
    sourceFiles: [MANUAL_SOURCE_LABEL],
    sourceBatchIds: [],
    entryMethod: "manual",
  }], options.codeRules)[0];
}
